import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { adminUpdateBookingCustomer } from '@/lib/api';

// Only edits CustomerDetails. Date/slot stay what the GCal event dictates.
const emptyCustomer = {
  name: '',
  email: '',
  phone: '',
  address: '',
  business_name: '',
  vehicle_make: '',
  vehicle_model: '',
  vehicle_year: '',
  vehicle_registration: '',
  notes: '',
  fuel_system: '',
  ecu_type: '',
  carb_type: '',
  aspiration: '',
  mod_list: '',
  known_faults: '',
};

const sanitizePrefill = (c) => {
  const next = { ...emptyCustomer };
  Object.keys(emptyCustomer).forEach((k) => {
    const v = c?.[k];
    if (v === null || v === undefined) return;
    next[k] = v;
  });
  // Blank out the "📅 ... tap to edit" placeholder so admin starts fresh
  if (typeof next.name === 'string' && next.name.startsWith('📅')) next.name = '';
  if (next.email === 'pending@nakitune.co.nz') next.email = '';
  if (next.phone === '(Pending)') next.phone = '';
  if (next.address === '(From Google Calendar)') next.address = '';
  if (next.vehicle_make === 'TBD') next.vehicle_make = '';
  if (next.vehicle_model === 'TBD') next.vehicle_model = '';
  if (next.vehicle_year === 'TBD') next.vehicle_year = '';
  return next;
};

export default function EditBookingDialog({ open, onOpenChange, booking, onSaved }) {
  const [form, setForm] = useState(emptyCustomer);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && booking) {
      setForm(sanitizePrefill(booking.customer || {}));
    }
  }, [open, booking]);

  if (!booking) return null;

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    // Minimum required to update: name, email, phone, vehicle
    const required = { name: 'Name', email: 'Email', phone: 'Phone', vehicle_make: 'Vehicle make', vehicle_model: 'Vehicle model', vehicle_year: 'Vehicle year' };
    for (const [k, label] of Object.entries(required)) {
      if (!form[k] || !String(form[k]).trim()) {
        toast.error(`${label} is required`);
        return;
      }
    }
    setSaving(true);
    try {
      // Send only non-empty fields
      const payload = {};
      Object.entries(form).forEach(([k, v]) => {
        if (v !== null && v !== undefined && String(v).trim() !== '') payload[k] = v;
      });
      // Address is required by backend CustomerDetails; ensure it has a value
      if (!payload.address) payload.address = '(not provided)';
      const res = await adminUpdateBookingCustomer(booking.id, payload);
      toast.success('Booking updated');
      onSaved?.(res.data?.booking);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update booking');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-['Barlow_Condensed'] text-xl font-bold uppercase text-white">
            Edit Booking Details
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            {booking.date} · {booking.slot_label}
            {booking.source === 'google_import' && (
              <span className="ml-2 inline-block px-2 py-0.5 bg-blue-900/40 border border-blue-700 text-blue-300 text-xs uppercase tracking-wider rounded">
                From Google Calendar
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Customer */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-zinc-300">Name *</Label>
              <Input value={form.name} onChange={(e) => update('name', e.target.value)} className="bg-zinc-950 border-zinc-800 text-zinc-200" data-testid="edit-booking-name" />
            </div>
            <div className="space-y-1">
              <Label className="text-zinc-300">Phone *</Label>
              <Input value={form.phone} onChange={(e) => update('phone', e.target.value)} className="bg-zinc-950 border-zinc-800 text-zinc-200" data-testid="edit-booking-phone" />
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label className="text-zinc-300">Email *</Label>
              <Input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} className="bg-zinc-950 border-zinc-800 text-zinc-200" data-testid="edit-booking-email" />
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label className="text-zinc-300">Address</Label>
              <Input value={form.address} onChange={(e) => update('address', e.target.value)} className="bg-zinc-950 border-zinc-800 text-zinc-200" data-testid="edit-booking-address" />
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label className="text-zinc-300">Business name</Label>
              <Input value={form.business_name} onChange={(e) => update('business_name', e.target.value)} className="bg-zinc-950 border-zinc-800 text-zinc-200" data-testid="edit-booking-business" />
            </div>
          </div>

          {/* Vehicle */}
          <div className="pt-4 border-t border-zinc-800">
            <h3 className="font-['Barlow_Condensed'] text-sm uppercase tracking-wider text-zinc-400 mb-3">Vehicle</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-zinc-300">Make *</Label>
                <Input value={form.vehicle_make} onChange={(e) => update('vehicle_make', e.target.value)} className="bg-zinc-950 border-zinc-800 text-zinc-200" data-testid="edit-booking-make" />
              </div>
              <div className="space-y-1">
                <Label className="text-zinc-300">Model *</Label>
                <Input value={form.vehicle_model} onChange={(e) => update('vehicle_model', e.target.value)} className="bg-zinc-950 border-zinc-800 text-zinc-200" data-testid="edit-booking-model" />
              </div>
              <div className="space-y-1">
                <Label className="text-zinc-300">Year *</Label>
                <Input value={form.vehicle_year} onChange={(e) => update('vehicle_year', e.target.value)} className="bg-zinc-950 border-zinc-800 text-zinc-200" data-testid="edit-booking-year" />
              </div>
              <div className="space-y-1 md:col-span-3">
                <Label className="text-zinc-300">Rego</Label>
                <Input value={form.vehicle_registration} onChange={(e) => update('vehicle_registration', e.target.value)} className="bg-zinc-950 border-zinc-800 text-zinc-200" data-testid="edit-booking-rego" />
              </div>
            </div>
          </div>

          {/* Engine */}
          <div className="pt-4 border-t border-zinc-800">
            <h3 className="font-['Barlow_Condensed'] text-sm uppercase tracking-wider text-zinc-400 mb-3">Engine Specs</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-zinc-300">Fuel system</Label>
                <Select value={form.fuel_system || ''} onValueChange={(v) => update('fuel_system', v)}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-200" data-testid="edit-booking-fuel">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-200">
                    <SelectItem value="EFI">EFI</SelectItem>
                    <SelectItem value="Carby">Carby</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.fuel_system === 'EFI' && (
                <div className="space-y-1">
                  <Label className="text-zinc-300">ECU type</Label>
                  <Input value={form.ecu_type} onChange={(e) => update('ecu_type', e.target.value)} placeholder="Link / Maxx / Haltech / Motec..." className="bg-zinc-950 border-zinc-800 text-zinc-200" data-testid="edit-booking-ecu" />
                </div>
              )}
              {form.fuel_system === 'Carby' && (
                <div className="space-y-1">
                  <Label className="text-zinc-300">Carb type</Label>
                  <Input value={form.carb_type} onChange={(e) => update('carb_type', e.target.value)} placeholder="Holley / Edelbrock / Other" className="bg-zinc-950 border-zinc-800 text-zinc-200" data-testid="edit-booking-carb" />
                </div>
              )}
              <div className="space-y-1">
                <Label className="text-zinc-300">Aspiration</Label>
                <Select value={form.aspiration || ''} onValueChange={(v) => update('aspiration', v)}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-200" data-testid="edit-booking-aspiration">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-200">
                    <SelectItem value="NA">NA</SelectItem>
                    <SelectItem value="Boosted">Boosted</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1 md:col-span-3">
                <Label className="text-zinc-300">Mod list</Label>
                <Textarea value={form.mod_list} onChange={(e) => update('mod_list', e.target.value)} rows={2} className="bg-zinc-950 border-zinc-800 text-zinc-200" data-testid="edit-booking-mods" />
              </div>
              <div className="space-y-1 md:col-span-3">
                <Label className="text-zinc-300">Known faults</Label>
                <Textarea value={form.known_faults} onChange={(e) => update('known_faults', e.target.value)} rows={2} className="bg-zinc-950 border-zinc-800 text-zinc-200" data-testid="edit-booking-faults" />
              </div>
              <div className="space-y-1 md:col-span-3">
                <Label className="text-zinc-300">Notes</Label>
                <Textarea value={form.notes} onChange={(e) => update('notes', e.target.value)} rows={2} className="bg-zinc-950 border-zinc-800 text-zinc-200" data-testid="edit-booking-notes" />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-zinc-700" data-testid="edit-booking-cancel">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-green-600 hover:bg-green-700" data-testid="edit-booking-save">
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
